/**
 * In-memory dev logger for debugging in Word Online task pane.
 *
 * Since console.log is not easily accessible in Office Add-in iframes,
 * this module captures log entries that can be displayed in a DevPanel.
 */

export interface LogEntry {
  id: number
  timestamp: string
  level: 'info' | 'warn' | 'error' | 'request' | 'response'
  message: string
  detail?: string
}

const MAX_ENTRIES = 100
let nextId = 1
let entries: LogEntry[] = []
let listeners: Array<() => void> = []

function notify() {
  for (const fn of listeners) fn()
}

function add(level: LogEntry['level'], message: string, detail?: string) {
  const entry: LogEntry = {
    id: nextId++,
    timestamp: new Date().toLocaleTimeString('nb-NO', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    level,
    message,
    detail,
  }
  entries = [...entries.slice(-(MAX_ENTRIES - 1)), entry]
  notify()
}

export const devLog = {
  info: (msg: string, detail?: string) => add('info', msg, detail),
  warn: (msg: string, detail?: string) => add('warn', msg, detail),
  error: (msg: string, detail?: string) => add('error', msg, detail),
  request: (msg: string, detail?: string) => add('request', msg, detail),
  response: (msg: string, detail?: string) => add('response', msg, detail),

  getEntries: () => entries,
  clear: () => { entries = []; notify() },

  subscribe: (fn: () => void) => {
    listeners.push(fn)
    return () => { listeners = listeners.filter((l) => l !== fn) }
  },
}
